/**
 * ============================================================================
 * UI HOOK - useUI
 * ============================================================================
 * Centralized UI state management (toasts, sounds, modals, etc.)
 * Version: v3.10.0-CLEAR-MAP
 */

import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const UIContext = createContext(null);

export const UIProvider = ({ children }) => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  // Toast notifications
  const [toasts, setToasts] = useState([]);
  
  // Sound system
  const [soundDebug, setSoundDebug] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved !== null ? saved === 'true' : true; // Default ON
  });
  const [soundVolume, setSoundVolume] = useState(() => {
    const saved = localStorage.getItem('soundVolume');
    return saved !== null ? parseFloat(saved) : 0.7; // Default 70%
  });
  const audioRefs = useRef({
    'camera-shutter': null,
    'level-up': null,
    'scan': null,
    'badge': null
  });
  
  // App updates
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  
  // Mobile & UI state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [submissionBanner, setSubmissionBanner] = useState(null);
  const [isQuickLocationOpen, setIsQuickLocationOpen] = useState(false);
  const [showAdditionalNotes, setShowAdditionalNotes] = useState(false);
  
  // Attachment modal
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  
  // ============================================================================
  // TOAST FUNCTIONS
  // ============================================================================
  
  const showToast = (message, type = 'success', duration = 4000) => {
    const id = Date.now();
    const toast = { id, message, type };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto-remove toast after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };
  
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  
  // ============================================================================
  // SOUND FUNCTIONS
  // ============================================================================
  
  const playSound = (soundType, trigger) => {
    // Check if sounds are enabled
    if (!soundEnabled) {
      return; // Silently skip if sounds are off
    }
    
    const timestamp = new Date().toLocaleTimeString();
    setSoundDebug(prev => [...prev, `${timestamp} - Attempting ${soundType} from ${trigger}`]);
    
    try {
      const audioElement = audioRefs.current[soundType];
      
      if (!audioElement) {
        setSoundDebug(prev => [...prev, `${timestamp} - ERROR: Audio element not found for ${soundType}`]);
        return;
      }
      
      // Reset audio to beginning if it's already playing
      audioElement.currentTime = 0;
      audioElement.volume = soundVolume; // Use dynamic volume
      
      // Play the preloaded audio
      audioElement.play()
        .then(() => {
          setSoundDebug(prev => [...prev, `${timestamp} - ${soundType} PLAYED! ✅`]);
        })
        .catch(e => {
          setSoundDebug(prev => [...prev, `${timestamp} - BLOCKED: ${e.name} - ${e.message}`]);
        });
    } catch (e) {
      setSoundDebug(prev => [...prev, `${timestamp} - CATCH ERROR: ${e.message}`]);
    }
  };
  
  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('soundEnabled', newState.toString());
    // Play confirmation sound if turning ON
    if (newState) {
      setTimeout(() => playSound('scan', 'sound-toggle-on'), 100);
    }
  };
  
  const setVolume = (volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume)); // Clamp between 0-1
    setSoundVolume(clampedVolume);
    localStorage.setItem('soundVolume', clampedVolume.toString());
  };
  
  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  /**
   * Listen for service worker updates
   */
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
          console.log('🔄 New version detected:', event.data.version);
          setShowUpdateNotification(true);
        }
      });
    }
  }, []);
  
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // State
    toasts,
    setToasts,
    soundDebug,
    setSoundDebug,
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
    audioRefs,
    showUpdateNotification,
    setShowUpdateNotification,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    submissionBanner,
    setSubmissionBanner,
    isQuickLocationOpen,
    setIsQuickLocationOpen,
    showAdditionalNotes,
    setShowAdditionalNotes,
    showAttachmentModal,
    setShowAttachmentModal,
    currentAttachment,
    setCurrentAttachment,
    
    // Functions
    showToast,
    removeToast,
    playSound,
    toggleSound,
    setVolume
  };
  
  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export default useUI;




